import {RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {paperclip} from '@sail/icons/react/Icon';
import {css, Icon, view} from '@sail/ui/deprecated';
import {animate as sailAnimate} from '@sail/engine/deprecated';
import {useAnalytics} from '../AnalyticsProvider';
import {ANIMATION_DURATION, useTheme} from '../index';
import {useIsMobileBreakpoints} from '../helpers/breakpoints';
import {InputProps} from './InputProps';
import {dotComColors, dotComTransitions} from '../theme/dotCom';
import {useDotComNoLlm} from '../FeatureFlagsProvider';

const messages = defineMessages({
  sendMessageAriaLabel: {
    id: 'conversations_ui.chat.input.sendButton.ariaLabel',
    description: 'Label for the chat textbox send button',
    defaultMessage: 'Send message',
  },
  selectAttachmentAriaLabel: {
    id: 'conversations_ui.chat.input.selectAttachment.ariaLabel',
    description: 'Label for the chat textbox select attachment',
    defaultMessage: 'Select attachment',
  },
});

export const HiddenFileInput = ({
  onChange,
  fileInputRef,
}: {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: RefObject<HTMLInputElement>;
}): JSX.Element => (
  <view.input
    data-testid="file-input"
    type="file"
    value=""
    onChange={onChange}
    css={{display: css.value('none')}}
    ref={fileInputRef}
  />
);

export const AttachmentButton = ({
  onSelect,
  handleFileInputKeyDown,
  fileInputRef,
}: {
  onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileInputKeyDown: (e: React.KeyboardEvent) => void;
  fileInputRef: RefObject<HTMLInputElement>;
}): JSX.Element => {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <view.label
      aria-label={intl.formatMessage(messages.selectAttachmentAriaLabel)}
      css={{
        border: css.value('none'),
        background: css.value('none'),
        display: css.value('flex'),
        cursor: css.value('pointer'),
        paddingY: 'xsmall',
        '&:focus-visible': {
          outline: css.value(`2px solid ${theme.linkColor}`),
          focusRing: css.value('none'),
        },
      }}
      onKeyDown={handleFileInputKeyDown}
      tabIndex={0}
    >
      <Icon
        css={{verticalAlign: css.value('center')}}
        icon={paperclip}
        data-testid="attachment-icon"
      />
      <HiddenFileInput onChange={onSelect} fileInputRef={fileInputRef} />
    </view.label>
  );
};

export const SendButton = ({
  onClick,
  disabled,
}: {
  onClick: (arg1: React.SyntheticEvent) => void;
  disabled?: boolean;
}): JSX.Element => {
  const intl = useIntl();
  const theme = useTheme();
  const color = disabled ? dotComColors.iconDisabled : dotComColors.brand400;

  return (
    <view.button
      aria-label={intl.formatMessage(messages.sendMessageAriaLabel)}
      data-testid="send-button"
      disabled={disabled}
      onClick={onClick}
      css={{
        border: css.value('none'),
        background: css.value('none'),
        display: css.value('flex'),
        cursor: css.value(disabled ? 'auto' : 'pointer'),
        paddingY: 'xsmall',
        color: css.value(color),
        transform: css.value('translateY(4px)'),
      }}
      type="button"
      tabIndex={-1}
    >
      <view.svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        tabIndex={disabled ? -1 : 0}
        css={{
          outline: css.value('none'),
          '&:focus-visible': {
            outline: css.value(`2px solid ${theme.linkColor}`),
            focusRing: css.value('none'),
          },
        }}
      >
        <view.path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.83074 14.3373L12.25 13.0746L7.87177 11.8237L7.02327 8.53151C6.9197 8.12967 7.17158 7.72246 7.58586 7.622C7.75839 7.58016 7.94052 7.5973 8.10156 7.67052L18.6499 12.4667C18.8636 12.5638 19 12.772 19 13.0009C19 13.2297 18.8636 13.4379 18.6499 13.535L8.10156 18.3312C7.7149 18.507 7.25453 18.3455 7.07328 17.9704C6.9978 17.8142 6.98013 17.6376 7.02327 17.4702L7.83074 14.3373Z"
          fill="currentColor"
          css={{
            transition: css.value(dotComTransitions.buttonTransition),
          }}
        />
      </view.svg>
    </view.button>
  );
};

const MIN_INPUT_HEIGHT = 42;
const MAX_INPUT_HEIGHT = 132;

// margin bottom (16px) + margin top (0)
const MARGIN_OFFSET = 16;

export function InputDotCom({
  disabled,
  attachmentDisabled,
  sendMessageDisabled,
  hidden,
  animate,
  placeholder,
  onSendMessage,
  onSendFile,
  onSendTyping,
}: InputProps) {
  const [enteredText, setEnteredText] = useState('');
  const analytics = useAnalytics();
  const theme = useTheme();
  const dotComNoLlm = useDotComNoLlm();

  const isMobile = useIsMobileBreakpoints();

  const textareaStyles = [];
  if (isMobile) {
    textareaStyles.push(
      css({
        // less than 16px causes iOS to zoom on focus, which breaks the experience
        fontSize: css.value('16px'),
      }),
    );
  }

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputContainer = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resizeTextArea = useCallback(() => {
    const textarea = textareaRef.current;
    const container = inputContainer.current;

    // important to include hidden here if nothing else to
    // trigger this to re-run when the input is re-rendered
    // after being un-hidden, otherwise it has scrollbars
    if (!hidden && container && textarea) {
      container.style.height = `${MIN_INPUT_HEIGHT}px`;

      const textAreaContentsHeight = textarea.scrollHeight;

      const cappedTextAreaHeight =
        Math.min(
          MAX_INPUT_HEIGHT,
          Math.max(MIN_INPUT_HEIGHT, textAreaContentsHeight),
        ) + MARGIN_OFFSET;

      container.style.height = `${cappedTextAreaHeight}px`;
    }
  }, [hidden, textareaRef, inputContainer]);

  const sendMessage = () => {
    if (!sendMessageDisabled) {
      const messageBody = enteredText.trim();
      setEnteredText('');

      if (messageBody) {
        onSendMessage(messageBody);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    // Note: We are intentionally using a deprecated property, keyCode, in support for IME(Input Method Editor) Enters.
    // Why: For some languages like Japanese, IME is used to transform qwerty inputs into characters, and the user needs to hit Enter to select.
    // However, we are also looking to send the user message on Enter. So we need a way to differentiate a normal Enter vs IME Enter.
    // In the deprecated properties like 'keyCode' and 'which', the two Enters are represented with codes '13' (normal) and '229' (IME).
    // In nondeprecated properties like 'key' and 'code', both are represented as 'Enter'.
    // Since keyCode is still supported in majority of the browsers today, and the legacy Copilot input used keyCode for the longest time, we'll continue to use keyCode here for now.
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();

      sendMessage();
    }
  };

  const handleSendButtonClick = (event: React.SyntheticEvent): void => {
    event.preventDefault();

    sendMessage();
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    const newEnteredText = event.target.value;
    setEnteredText(newEnteredText);
    if (newEnteredText.length) {
      onSendTyping();
    }
  };

  const handleFileSelection = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    analytics.action('file_attachment_sent');
    e.target.files && onSendFile && onSendFile(e.target.files[0]);
  };

  const handleFileInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  useEffect(() => {
    resizeTextArea();
  }, [enteredText, resizeTextArea]);

  const showSendButton = enteredText || !onSendFile || attachmentDisabled;

  const inputDisabled = !!(hidden || disabled);

  if (dotComNoLlm && hidden) {
    return;
  }

  return (
    <view.div
      data-testid="text-input-container"
      ref={inputContainer}
      css={{
        position: css.value('relative'),
        flexGrow: css.value(0),
        flexShrink: css.value(0),
        height: css.value(`${MIN_INPUT_HEIGHT + MARGIN_OFFSET}px`),
        stack: 'y',
        '&:focus-within button': {
          color: css.value('#4F5B76'),
        },
      }}
      uses={
        animate
          ? [
              sailAnimate.in({
                duration: ANIMATION_DURATION,
                transform: 'translateY(calc(100% * 1))',
                easing: 'ease-out',
              }),
              sailAnimate.out({
                duration: ANIMATION_DURATION,
                transform: 'translateY(calc(120% * 1))',
                easing: 'ease-in',
              }),
            ]
          : []
      }
    >
      <view.textarea
        data-testid="text-input"
        disabled={inputDisabled}
        value={enteredText}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={placeholder}
        ref={textareaRef}
        uses={textareaStyles}
        css={{
          fontSize: css.value('14px'),
          resize: css.value('none'),
          background: css.value(dotComColors.brand700),
          border: css.value('none'),
          borderRadius: 'medium',
          boxShadow: 'none',
          marginRight: 'medium',
          marginLeft: 'medium',
          marginTop: 0,
          marginBottom: 'medium',
          paddingTop: css.value('10px'),
          paddingRight: css.value('42px'),
          paddingBottom: css.value('12px'),
          paddingLeft: css.value('14px'),
          position: css.value('absolute'),
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          boxSizing: css.value('border-box'),
          lineHeight: css.value('20px'),
          color: inputDisabled
            ? css.value(dotComColors.inputTextDisabled)
            : css.value(dotComColors.brand500),
          whiteSpace: css.value('normal'),
          '&:focus-visible': {
            outline: css.value(`1px solid ${theme.linkColor}`),
          },
          '&::placeholder': {
            color: inputDisabled
              ? css.value(dotComColors.inputTextDisabled)
              : css.value(dotComColors.brand800),
          },
        }}
      />
      <view.div
        data-testid="text-input-icon-container"
        css={{
          position: css.value('absolute'),
          bottom: css.value('25px'),
          right: css.value('24px'),
          padding: 1,
        }}
      >
        {showSendButton ? (
          <SendButton
            onClick={handleSendButtonClick}
            disabled={inputDisabled || sendMessageDisabled}
          />
        ) : (
          <AttachmentButton
            onSelect={handleFileSelection}
            handleFileInputKeyDown={handleFileInputKeyDown}
            fileInputRef={fileInputRef}
          />
        )}
      </view.div>
    </view.div>
  );
}
